"use client";

import React, { useState, useEffect, useCallback } from "react";

interface OfflineIndicatorProps {
  showToast?: boolean;
  position?: "top" | "bottom";
}

export function OfflineIndicator({
  showToast = true,
  position = "bottom",
}: OfflineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (isOnline && !wasOffline) return null;

  if (showToast) {
    return (
      <div
        className={`fixed ${position === "top" ? "top-4" : "bottom-4"} left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2 rounded-full bg-destructive/90 px-4 py-2 text-sm font-medium text-destructive-foreground shadow-lg backdrop-blur-sm transition-all duration-300 ${
          wasOffline ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
        }`}
        role="status"
        aria-live="polite"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
        {wasOffline ? "Connection restored" : "You are offline"}
      </div>
    );
  }

  return (
    <div
      className={`fixed ${position === "top" ? "top-0" : "bottom-0"} left-0 right-0 z-[200] h-1 bg-destructive transition-all duration-300 ${
        wasOffline ? "h-0" : "h-1"
      }`}
      role="status"
      aria-live="polite"
    />
  );
}